import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import UsersApp from './UsersApp'
import { addNewUser, deleteUser, useReturnCurrentUser, useReturnSingleUsers } from './userSlice'
import { updateGlobalLoaderStatus, updateQuickNotificationDetails, useReturnGlobalLoaderStatus } from '../notifications/notificationsSlice'
import { IUserModel } from '../../services/user'
import { handleRequestError } from '../../services/api'
import PageHeadSection from '../admin/components/PageHeadSection'
import SectionBoxContainer from '../../components/SectionBoxContainer'
import NotFound from '../../components/NotFound'
import BasicDataViewer, { IBasicDataViewItem } from '../../components/DataDisplayer/BasicDataViewer'
import { Avatar } from '@mui/material'
import GenerateIcon from '../../components/GenerateIcon'
import BlockUserButton from './BlockUserButton'
import BanUserButton from './BanUserButton'

interface IViewSingleUserPageProps {
    customId?: number
}

export default function ViewSingleUserPage(props: IViewSingleUserPageProps) {

    const { customId } = props

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const params = useParams()
    const userApp = new UsersApp()

    const userId = customId ? customId : params.id ? Number(params.id) : 0

    const user = useReturnSingleUsers(userId)

    const [notFound, setNotFound] = useState<boolean>(false)

    const [undoTimeout, setUndoTimeout] = useState<NodeJS.Timeout | null>(null)
    const [basicDataViewerItems, setBasicDataViewerItems] = useState<IBasicDataViewItem[]>([])

    const uploadLoader = (status: boolean) => dispatch(updateGlobalLoaderStatus({ status: status }))
    const loading = useReturnGlobalLoaderStatus()

    const currentUser = useReturnCurrentUser()

    const [managePersmission, setManagePersmission] = useState<boolean>(false)

    /**
     * Load item
     */
    const loadUser = () => {
        uploadLoader(true)

        userApp.singleInstanceDetails(userId)
            .then(response => {

                const result: IUserModel = response.data
                // setItem(result)

                dispatch(addNewUser(result))

                setNotFound(false)
                uploadLoader(false)

            })
            .catch(error => {

                const { status } = error.response

                handleRequestError(error, dispatch)

                if (status == 404){
                    setNotFound(true)
                }

                uploadLoader(false)

            })
    }

    /**
     * Handle before delete
     */
    const handleBeforeDelete = () => {
        setNotFound(false)

        if (undoTimeout){
            clearTimeout(undoTimeout)
        }
    }

    /**
     * Delete item
     */
    const handleDeleteItem = () => {
    

        setNotFound(true)

        updateQuickNotificationDetails(true, "User has been deleted", "error", dispatch, () => {}, 5000, false, [
            // {
            //     label: "Close",
            //     handleClose: true
            // },
            {
                label: "Undo",
                handleClose: false,
                onClick: () => handleBeforeDelete()
            }
        ])

        const timeout = setTimeout(() => {
            userApp.deleteInstance(user.id)
            .then(response => {
                dispatch(deleteUser({ id: user.id }))
                navigate("/panel/users")
            })
            .catch(error => {
                handleRequestError(error, dispatch)
            })
        }, 5000);
    
        setUndoTimeout(timeout);

    }

    /**
     * Load item
     */
    useEffect(() => {

        if (userId){

            if (user.id !== userId){
                loadUser()
            }

        }

    }, [userId])

    useEffect(() => {

        setBasicDataViewerItems([
            {
                type: "text",
                label: "First Name",
                value: user.first_name
            },
            {
                type: "text",
                label: "Last Name",
                value: user.last_name
            },
            {
                type: "status",
                label: "Status",
                value: user.status
            },
            {
                type: "status",
                label: "Type",
                value: user.type
            },
            {
                type: "text",
                label: "Email",
                value: user.email
            },
            {
                type: "text",
                label: "Password Set",
                value: user.is_password_set ? "Yes" : "No",
                privateDetails: managePersmission ? false : true
            },
            {
                type: "text",
                label: "2FA",
                value: user.totp_enabled ? "Enabled" : "Disabled",
                privateDetails: managePersmission ? false : true,
            },
            {
                type: "date",
                label: "Date joined",
                value: user.date_joined ? user.date_joined : "",
                privateDetails: managePersmission ? false : true
            },
            {
                type: "date",
                label: "Last login",
                value: user.last_login ? user.last_login : "",
                privateDetails: managePersmission ? false : true
            },
        ])


    }, [user, managePersmission])

    useEffect(() => {
        if (currentUser.is_superuser || currentUser.is_daemon){
            setManagePersmission(true)
        }
    }, [currentUser])

    return(
        <div>
            <PageHeadSection 
                pageName="users" 
                customTitle={notFound ? "404" : user.full_name ? user.full_name : ""} 
                customActionButtons={managePersmission ? [
                    {
                        icon: "delete",
                        variant: "text",
                        label: "Delete",
                        color: "error",
                        onClick: () => handleDeleteItem()
                    },
                    {
                        icon: "edit",
                        variant: "text",
                        link: `/panel/users/edit/${user.id}`,
                        label: "edit",
                        color: "success"
                    }
                ] : []}
            />

            {notFound && <SectionBoxContainer><NotFound message="User doesn't exist." /></SectionBoxContainer>}

            {!notFound && (
                <div className="admin-single-item-view-page-container">
                    <div className="content-of-admin-single-item-view-page-container">

                        <div className="sections-of-admin-single-item-view-page-container">
                            <div className="content-of-sections-of-admin-single-item-view-page-container">

                                <div className="section-of-admin-single-item-view-page-container thumbnail user-thumbnail">
                                    <div className="content-of-section-of-admin-single-item-view-page-container">
                                        <SectionBoxContainer>
                                            <Avatar src={user.thumbnail}><GenerateIcon icon="user" /></Avatar>
                                        </SectionBoxContainer>
                                    </div>
                                </div>

                                <div className="section-of-admin-single-item-view-page-container text">
                                    <div className="content-of-section-of-admin-single-item-view-page-container">
                                        <SectionBoxContainer showTitle title="Details" description="Here are the user's details">
                                            <BasicDataViewer items={basicDataViewerItems} />

                                            {managePersmission && (
                                                <div className="action-buttons-of-section-box-admin-panel">
                                                    <div className="content-of-action-buttons-of-section-box-admin-panel">

                                                        <BlockUserButton user={user} />
                                                        <BanUserButton user={user} />

                                                    </div>
                                                </div>
                                            )}
                                        </SectionBoxContainer>
                                    </div>
                                </div>
                            </div>
                        </div>


                    </div>
                </div>
            )}

        </div>
    )
}